import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IoClose } from 'react-icons/io5';
import { FaCalendarAlt, FaHistory, FaCheckCircle, FaAward, FaFilePdf, FaImages, FaInstagram, FaCrown, FaMicrophone, FaTrashAlt } from 'react-icons/fa';

interface EventModalProps {
  eventData?: any | null;
  selectedDate?: Date | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EventModal: React.FC<EventModalProps> = ({ eventData, selectedDate, onClose, onSuccess }) => {
  const [eventTimeline, setEventTimeline] = useState<'future' | 'past'>('future');
  const [members, setMembers] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    event_name: '',
    location: '',
    chapter: 'RMB Jaipur',
    event_type: 'General Meeting',
    event_date: '',
    description: '',
    rmbian_of_meeting: '',
    presentation_1: '',
    presentation_2: '',
    instagram_reel: '',
    speaker_designation: ''
  });

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [photos, setPhotos] = useState<FileList | null>(null);
  const [momPdf, setMomPdf] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setMembers(list.filter((u: any) => u.role !== 'super-admin' && u.delete !== 1 && u.name && u.name.trim().toLowerCase() !== 'admin'));
      })
      .catch(err => console.error("Error fetching members in EventModal:", err));
  }, []);

  const matchMemberName = (val: any, membersList: any[]) => {
    if (!val) return '';
    const strVal = String(val).trim();
    if (!strVal) return '';

    // 1. Try match by ID
    const byId = membersList.find(m => String(m.id) === strVal);
    if (byId && byId.name) return byId.name.trim();

    // 2. Try match exact name
    const exact = membersList.find(m => m.name && m.name.trim().toLowerCase() === strVal.toLowerCase());
    if (exact) return exact.name.trim();

    // 3. Try match substring (e.g. "Anil Jain (Moduler Kitchen)" -> "Anil Jain")
    const partial = membersList.find(m => m.name && (strVal.toLowerCase().includes(m.name.trim().toLowerCase()) || m.name.trim().toLowerCase().includes(strVal.toLowerCase())));
    if (partial) return partial.name.trim();

    return strVal;
  };

  const formatLocalDatetime = (rawDate: any) => {
    if (!rawDate) return '';
    let str = String(rawDate).trim();
    if (str.includes(' ') && !str.includes('T')) {
      str = str.replace(' ', 'T');
    }
    const d = new Date(str);
    if (isNaN(d.getTime())) return '';

    const pad = (n: number) => (n < 10 ? '0' + n : n);
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (eventData) {
      const rawDate = eventData.event_date || eventData.start_date || eventData.date;
      const formattedDate = formatLocalDatetime(rawDate);

      let timeline: 'future' | 'past' = 'future';
      if (rawDate) {
        let str = String(rawDate).trim();
        if (str.includes(' ') && !str.includes('T')) str = str.replace(' ', 'T');
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          const now = new Date();
          if (d < now) {
            timeline = 'past';
          }
        }
      }

      const p1Raw = eventData.presentation_1 || eventData.Presentation_First || '';
      const p2Raw = eventData.presentation_2 || eventData.Presentation_Second || '';

      setEventTimeline(timeline);
      setFormData({
        event_name: eventData.heading || eventData.title || eventData.description || eventData.content || '',
        location: eventData.location || eventData.venue || '',
        chapter: eventData.chapter || 'RMB Jaipur',
        event_type: eventData.event_type || 'General Meeting',
        event_date: formattedDate,
        description: eventData.description || eventData.content || eventData.heading || eventData.title || '',
        rmbian_of_meeting: String(eventData.rmbian_of_meeting || eventData.RMBiansOfMeeting || ''),
        presentation_1: members.length > 0 ? matchMemberName(p1Raw, members) : p1Raw,
        presentation_2: members.length > 0 ? matchMemberName(p2Raw, members) : p2Raw,
        instagram_reel: eventData.instagram_reel || '',
        speaker_designation: eventData.speaker_designation || ''
      });
    } else if (selectedDate) {
      const formattedDate = formatLocalDatetime(selectedDate);
      const now = new Date();
      setEventTimeline(selectedDate < now ? 'past' : 'future');
      setFormData(prev => ({ ...prev, event_date: formattedDate }));
    } else {
      // Default future event to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      setFormData(prev => ({ ...prev, event_date: formatLocalDatetime(tomorrow) }));
    }
  }, [eventData, selectedDate, members]);

  const handleTimelineChange = (timeline: 'future' | 'past') => {
    setEventTimeline(timeline);
    const now = new Date();

    if (timeline === 'past') {
      const current = formData.event_date ? new Date(formData.event_date) : now;
      if (current >= now) {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(18, 0, 0, 0);
        setFormData(prev => ({ ...prev, event_date: yesterday.toISOString().slice(0, 16) }));
      }
    } else {
      const current = formData.event_date ? new Date(formData.event_date) : now;
      if (current < now) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        setFormData(prev => ({ ...prev, event_date: tomorrow.toISOString().slice(0, 16) }));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDelete = async () => {
    if (!eventData || !eventData.id) return;
    if (!window.confirm(`Are you sure you want to delete "${formData.event_name || 'this event'}"?`)) return;

    setLoading(true);
    try {
      const rawId = String(eventData.id).replace('news-', '');
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/events/${rawId}`).catch(() => {});
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/news/${rawId}`).catch(() => {});
      setLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      data.append('heading', formData.event_name || formData.description || 'Chapter Event');
      data.append('title', formData.event_name || formData.description || 'Chapter Event');
      data.append('location', formData.location);
      data.append('chapter', formData.chapter);
      data.append('event_type', formData.event_type);
      data.append('event_date', formData.event_date);
      data.append('description', formData.description || formData.event_name);
      data.append('rmbian_of_meeting', eventTimeline === 'past' ? formData.rmbian_of_meeting : '');

      let pres1 = formData.presentation_1;
      let pres2 = formData.presentation_2;

      // Handle Webinar speaker designation formatting
      if (formData.event_type === 'Webinar' && formData.speaker_designation) {
        if (pres1 && !pres1.includes(formData.speaker_designation)) {
          pres1 = `${pres1} (${formData.speaker_designation})`;
        }
      }

      // Handle Social event (no presentation 1/2)
      if (formData.event_type === 'Social') {
        pres1 = '';
        pres2 = '';
      }

      data.append('presentation_1', pres1);
      data.append('presentation_2', pres2);

      if (eventTimeline === 'past' && formData.instagram_reel) {
        data.append('instagram_reel', formData.instagram_reel);
      }

      if (thumbnail) {
        data.append('thumbnail', thumbnail);
        data.append('image', thumbnail);
      }

      if (eventTimeline === 'past' && photos) {
        for (let i = 0; i < photos.length; i++) {
          data.append('photos', photos[i]);
        }
      }

      if (eventTimeline === 'past' && momPdf) {
        data.append('mom_pdf', momPdf);
      }

      if (eventData && eventData.id) {
        if (eventData.photographs) {
          data.append('existing_photographs', eventData.photographs);
        }
        if (eventData.mom_pdf) {
          data.append('existing_mom_pdf', eventData.mom_pdf);
        }
        const rawId = String(eventData.id).replace('news-', '');
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/events/${rawId}`, data);
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/events', data);
      }

      setLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving event:', err);
      setError(err.response?.data?.error || 'Failed to save event. Please check inputs.');
      setLoading(false);
    }
  };

  const renderEventTypeSpecificFields = () => {
    switch (formData.event_type) {
      case 'Installation':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50/70 p-3.5 rounded-xl border border-purple-200">
            <div>
              <label className="block text-xs font-bold text-purple-900 mb-1 flex items-center gap-1.5">
                <FaCrown className="text-amber-500 text-sm" /> Chief Guest Name
              </label>
              <input
                type="text"
                name="presentation_1"
                placeholder="e.g. Rtn. Sunil Kumar Gupta (District Governor)"
                value={formData.presentation_1}
                onChange={handleChange}
                className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-purple-900 mb-1 flex items-center gap-1.5">
                🏢 Chief Guest Company / Organization
              </label>
              <input
                type="text"
                name="presentation_2"
                placeholder="e.g. Rotary District 3054 / Apex Group"
                value={formData.presentation_2}
                onChange={handleChange}
                className="w-full p-2.5 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm font-semibold bg-white"
              />
            </div>
          </div>
        );

      case 'Webinar':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-blue-50/70 p-3.5 rounded-xl border border-blue-200">
            <div>
              <label className="block text-xs font-bold text-blue-900 mb-1 flex items-center gap-1.5">
                <FaMicrophone className="text-blue-600 text-sm" /> Speaker Name
              </label>
              <input
                type="text"
                name="presentation_1"
                placeholder="e.g. Dr. Rajesh Sharma"
                value={formData.presentation_1}
                onChange={handleChange}
                className="w-full p-2.5 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-blue-900 mb-1 flex items-center gap-1.5">
                🏢 Speaker Company Name
              </label>
              <input
                type="text"
                name="presentation_2"
                placeholder="e.g. Global Tech Solutions"
                value={formData.presentation_2}
                onChange={handleChange}
                className="w-full p-2.5 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-blue-900 mb-1 flex items-center gap-1.5">
                🎓 Speaker Designation / Role
              </label>
              <input
                type="text"
                name="speaker_designation"
                placeholder="e.g. Managing Director & Keynote Speaker"
                value={formData.speaker_designation}
                onChange={handleChange}
                className="w-full p-2.5 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
              />
            </div>
          </div>
        );

      case 'Social':
        return null; // NO presentation options for Social event type!

      case 'Field Visit':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-emerald-50/70 p-3.5 rounded-xl border border-emerald-200">
            <div>
              <label className="block text-xs font-bold text-emerald-900 mb-1 flex items-center gap-1.5">
                📍 Field Visit Member Name
              </label>
              <select
                name="presentation_1"
                value={formData.presentation_1}
                onChange={(e) => {
                  const selectedMember = e.target.value;
                  const userObj = members.find((m: any) => (m.name ? m.name.trim() : '') === selectedMember);
                  setFormData((prev: any) => ({
                    ...prev,
                    presentation_1: selectedMember,
                    presentation_2: userObj?.company_name || prev.presentation_2
                  }));
                }}
                className="w-full p-2.5 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-bold bg-white text-slate-800"
              >
                <option value="">-- Select Field Visit Member --</option>
                {formData.presentation_1 && !members.some(m => (m.name ? m.name.trim() : '') === formData.presentation_1) && (
                  <option value={formData.presentation_1}>{formData.presentation_1}</option>
                )}
                {members.map((m: any) => (
                  <option key={m.id} value={m.name ? m.name.trim() : `Member #${m.id}`}>
                    {m.name ? m.name.trim() : `Member #${m.id}`} ({m.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-emerald-900 mb-1 flex items-center gap-1.5">
                🏢 Business Name / Host Company
              </label>
              <input
                type="text"
                name="presentation_2"
                placeholder="e.g. Aadvik Tech Services"
                value={formData.presentation_2}
                onChange={handleChange}
                className="w-full p-2.5 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-semibold bg-white"
              />
            </div>
          </div>
        );

      default: // General Meeting, Meeting, Other
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Presentation 1 (Active Member Speaker)</label>
              <select
                name="presentation_1"
                value={formData.presentation_1}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
              >
                <option value="">-- Select Active Member --</option>
                {formData.presentation_1 && !members.some(m => (m.name ? m.name.trim() : '') === formData.presentation_1) && (
                  <option value={formData.presentation_1}>{formData.presentation_1}</option>
                )}
                {members.map((m: any) => (
                  <option key={m.id} value={m.name ? m.name.trim() : `Member #${m.id}`}>
                    {m.name ? m.name.trim() : `Member #${m.id}`} ({m.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Presentation 2 (Active Member Speaker)</label>
              <select
                name="presentation_2"
                value={formData.presentation_2}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
              >
                <option value="">-- Select Active Member --</option>
                {formData.presentation_2 && !members.some(m => (m.name ? m.name.trim() : '') === formData.presentation_2) && (
                  <option value={formData.presentation_2}>{formData.presentation_2}</option>
                )}
                {members.map((m: any) => (
                  <option key={m.id} value={m.name ? m.name.trim() : `Member #${m.id}`}>
                    {m.name ? m.name.trim() : `Member #${m.id}`} ({m.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 p-4 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto relative p-6 border border-slate-200">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-800 p-1.5 rounded-full hover:bg-slate-100 transition"
        >
          <IoClose size={24} />
        </button>

        <div className="mb-4">
          <span className="text-xs font-black uppercase text-amber-600 tracking-wider block">Admin Management</span>
          <h2 className="text-2xl font-black text-slate-800">
            {eventData ? '✏️ Edit / Manage Event' : '➕ Create New Event'}
          </h2>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* EVENT TIMELINE CATEGORY SELECTOR (FUTURE / PAST) */}
        <div className="space-y-1 mb-5">
          <label className="block text-xs font-bold uppercase text-slate-500 tracking-wider">Select Event Category & Timeline</label>
          <div className="bg-slate-100 p-1.5 rounded-xl flex gap-2 border border-slate-200">
            <button
              type="button"
              onClick={() => handleTimelineChange('future')}
              className={`flex-1 py-2.5 px-4 rounded-lg font-black text-xs transition flex items-center justify-center gap-2 cursor-pointer ${
                eventTimeline === 'future'
                  ? 'bg-[#02256D] text-[#FDBC12] shadow-md border border-[#02256D]'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              <FaCalendarAlt className="text-sm" />
              <span>🗓️ Future / Upcoming Event</span>
            </button>

            <button
              type="button"
              onClick={() => handleTimelineChange('past')}
              className={`flex-1 py-2.5 px-4 rounded-lg font-black text-xs transition flex items-center justify-center gap-2 cursor-pointer ${
                eventTimeline === 'past'
                  ? 'bg-[#0A2451] text-[#FDBC12] shadow-md border border-[#0A2451]'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              <FaHistory className="text-sm" />
              <span>📜 Past Event / Highlights</span>
            </button>
          </div>
          <p className="text-[11px] text-slate-500 font-medium px-1">
            {eventTimeline === 'future' 
              ? '✨ Upcoming events will be featured in the "Upcoming General Meetings & Events" section.'
              : '📜 Past events will be published under "Learn About Our Latest News & Past Highlights".'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* EVENT NAME / TITLE FIELD */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              📌 Event Title / Name of the Event <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="event_name"
              placeholder="e.g. 127th Joint Installation Ceremony / General Meeting 126"
              value={formData.event_name}
              onChange={handleChange}
              className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold text-[#02256D]"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Event Type</label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold bg-white"
                required
              >
                <option value="General Meeting">General Meeting</option>
                <option value="Meeting">Meeting</option>
                <option value="Field Visit">Field Visit</option>
                <option value="Installation">Installation</option>
                <option value="Webinar">Webinar</option>
                <option value="Social">Social</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                {eventTimeline === 'past' ? '📅 Past Event Date & Time' : '🗓️ Scheduled Event Date & Time'}
              </label>
              <input
                type="datetime-local"
                name="event_date"
                value={formData.event_date}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Location / Venue</label>
              <input
                type="text"
                name="location"
                placeholder="e.g. Hotel Rajputana Sheraton, Jaipur"
                value={formData.location}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Chapter Name</label>
              <input
                type="text"
                name="chapter"
                placeholder="e.g. RMB Jaipur"
                value={formData.chapter}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
              />
            </div>
          </div>

          {/* DYNAMIC EVENT-TYPE SPECIFIC FIELDS (Installation, Webinar, Social, Field Visit, General Meeting) */}
          {renderEventTypeSpecificFields()}

          {/* RMBian of the Meeting (ONLY FOR PAST EVENTS) */}
          {eventTimeline === 'past' && (
            <div className="bg-amber-50/70 p-3.5 rounded-xl border border-amber-200 space-y-1">
              <label className="block text-xs font-bold text-amber-900 flex items-center gap-1.5">
                <FaAward className="text-amber-600 text-sm" /> 🏆 RMBian Of Meeting (Honored Member)
              </label>
              <select
                name="rmbian_of_meeting"
                value={formData.rmbian_of_meeting}
                onChange={handleChange}
                className="w-full p-2.5 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold bg-white text-slate-800"
              >
                <option value="">-- Select RMBian of the Meeting --</option>
                {formData.rmbian_of_meeting && !members.some(m => String(m.id) === String(formData.rmbian_of_meeting)) && (
                  <option value={formData.rmbian_of_meeting}>Member #{formData.rmbian_of_meeting}</option>
                )}
                {members.map((m: any) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.name ? m.name.trim() : `Member #${m.id}`} ({m.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Instagram Reel Link (ONLY FOR PAST EVENTS) */}
          {eventTimeline === 'past' && (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                <FaInstagram className="text-pink-600 text-sm" /> Instagram Reel Link (Optional)
              </label>
              <input
                type="text"
                name="instagram_reel"
                placeholder="e.g. https://www.instagram.com/reel/C..."
                value={formData.instagram_reel}
                onChange={handleChange}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none text-sm font-medium"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Event Agenda / Description</label>
            <textarea
              name="description"
              rows={3}
              placeholder="Enter meeting agenda, highlights, or description..."
              value={formData.description}
              onChange={handleChange}
              className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
            />
          </div>

          {/* DYNAMIC FILE UPLOADS: Future Event = Thumbnail Only | Past Event = Thumbnail, Gallery & MoM PDF */}
          <div className="pt-2">
            {eventTimeline === 'future' ? (
              <div className="bg-amber-50/60 p-3.5 rounded-xl border border-amber-200 space-y-1">
                <label className="block text-xs font-bold text-amber-900 flex items-center gap-1.5">
                  <FaImages className="text-amber-600 text-sm" /> 📷 Event Thumbnail Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnail(e.target.files ? e.target.files[0] : null)}
                  className="w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                />
                <p className="text-[11px] text-amber-800 font-medium">Cover photo shown on upcoming event card.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-amber-50/60 p-3 rounded-xl border border-amber-200 space-y-1">
                  <label className="block text-xs font-bold text-amber-900 flex items-center gap-1.5">
                    <FaImages className="text-amber-600 text-sm" /> 📷 Event Thumbnail
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnail(e.target.files ? e.target.files[0] : null)}
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                  />
                  <p className="text-[10px] text-amber-800 font-medium">Primary cover photo.</p>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                  <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <FaImages className="text-blue-600" /> Event Gallery Photos
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setPhotos(e.target.files)}
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-100 file:text-blue-800 hover:file:bg-blue-200 cursor-pointer"
                  />
                  <p className="text-[10px] text-slate-500">Multiple gallery photos.</p>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-1">
                  <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <FaFilePdf className="text-rose-600" /> MoM PDF Document
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setMomPdf(e.target.files ? e.target.files[0] : null)}
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-rose-100 file:text-rose-800 hover:file:bg-rose-200 cursor-pointer"
                  />
                  <p className="text-[10px] text-slate-500">Minutes of Meeting PDF.</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-6 border-t border-slate-200">
            <div>
              {eventData && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-xl transition text-xs flex items-center gap-1.5 cursor-pointer border border-rose-200"
                >
                  <FaTrashAlt /> Delete Event
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-xl transition text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-[#02256D] hover:bg-blue-900 text-[#FDBC12] font-black rounded-xl shadow-lg transition disabled:opacity-50 text-xs flex items-center gap-2 cursor-pointer"
              >
                <FaCheckCircle />
                {loading ? 'Saving...' : eventData ? 'Update Event' : `Save ${eventTimeline === 'past' ? 'Past' : 'Future'} Event`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;
